import { useNavigate } from 'react-router-dom'
import { Plus, Lock } from 'lucide-react'
import { Curation } from '../../lib/curations'

interface CurationsGridProps {
  curations: Curation[]
  isOwnProfile: boolean
  username: string
  onNewCuration?: () => void
}

export function CurationsGrid({ curations, isOwnProfile, username, onNewCuration }: CurationsGridProps) {
  const navigate = useNavigate()

  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString('en-US', {
  //     month: 'short',
  //     day: 'numeric',
  //     year: 'numeric'
  //   })
  // }

  // Empty state for own profile
  if (curations.length === 0 && isOwnProfile) {
    return (
      <div className="w-full px-4 py-12">
        <div className="max-w-md mx-auto text-center">
          <h3 className="text-2xl font-light mb-3" style={{ fontFamily: "Canela, Georgia, serif" }}>
            Start your first curation
          </h3>
          <p className="text-gray-600 mb-6" style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}>
            Collect products, brands, and stories into one place.
          </p>
          <button
            onClick={onNewCuration}
            className="px-6 py-3 bg-black text-white rounded-full hover:bg-gray-800 transition-colors flex items-center gap-2 mx-auto"
            style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
          >
            <Plus className="h-5 w-5" />
            <span className="font-medium">New Curation</span>
          </button>
        </div>
      </div>
    )
  }

  // Empty state for other users
  if (curations.length === 0) {
    return (
      <div className="w-full px-4 py-12">
        <div className="text-center text-gray-500" style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}>
          No curations yet
        </div>
      </div>
    )
  }

  return (
    <div className="w-full px-4 pb-12">
      <div className="max-w-[1400px] mx-auto">
        {/* Curations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-8">
          {curations.map((curation) => (
            <div
              key={curation.id}
              className="cursor-pointer group"
              onClick={() => navigate(`/profile/${username}/issues/${curation.slug}`)}
            >
              {/* Cover Image */}
              <div className={`aspect-[4/3] overflow-hidden bg-gray-100 mb-3 relative ${
                curation.rounded_corners ? 'rounded-lg' : ''
              }`}>
                {/* Private indicator — owner's own grid only: a lock on private
                    boards, nothing on public or published. */}
                {isOwnProfile && curation.status === 'private' && (
                  <div
                    className="absolute top-2 right-2 z-10 flex items-center justify-center h-6 w-6 rounded-full bg-white/90 backdrop-blur shadow-sm"
                    title="Private — only you can see this"
                  >
                    <Lock className="h-3.5 w-3.5" style={{ color: '#57241F' }} />
                  </div>
                )}
                {curation.highlighted_image_url ? (
                  <>
                    <img
                      src={curation.highlighted_image_url}
                      alt={curation.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 pointer-events-none" />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <span className="text-sm" style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}>
                        No cover image
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Title - Matching detail page style */}
              <h3
                className="text-2xl font-light mb-2 group-hover:opacity-70 transition-opacity"
                style={{ fontFamily: "Canela, Georgia, serif" }}
              >
                {curation.title}
              </h3>

              {/* Description - Matching detail page style */}
              {curation.narrative && (
                <p
                  className="text-gray-700 line-clamp-2 mb-2 text-base leading-relaxed"
                  style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
                >
                  {curation.narrative}
                </p>
              )}

              {/* Meta info */}
              <div
                className="text-sm text-gray-600"
                style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
              >
                {curation.item_count || 0} items
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
